import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/text-button.js';
import { icon } from '../lib/icons';
import { t, LocaleController } from '../lib/i18n';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * <pwa-prompt> — install + update affordances (spec.nfc-pwa §7):
 *   - Android/Chrome: capture `beforeinstallprompt`, offer a styled install button.
 *   - iOS Safari: no `beforeinstallprompt` — show manual Add-to-Home instructions.
 *   - Update: main.ts calls `showUpdate()` when the service worker has a new
 *     version; we surface a Reload prompt.
 */
@customElement('pwa-prompt')
export class PwaPrompt extends LitElement {
  @state() private installEvt: BeforeInstallPromptEvent | null = null;
  /** How long a willing browser gets to fire `beforeinstallprompt` before the
   *  manual fallback shows. Overridable so tests need not wait it out. */
  static fallbackDelayMs = 4000;
  @state() private fallbackReady = false;
  private fallbackTimer = 0;
  // Re-renders this element when the interface language changes.
  readonly i18n = new LocaleController(this);
  @state() private updateReady = false;
  @state() private dismissed = false;

  /** Called by main.ts's registerSW onNeedRefresh. */
  onReload: () => void = () => location.reload();

  static styles = css`
    :host { position:fixed; left:0; right:0; bottom:0; z-index:50; display:flex; justify-content:center; pointer-events:none;
      /* The banner owns the very bottom edge, full width; the floating
         buttons read its measured height (--install-bar-h) and sit above it,
         dropping back down when it is dismissed (operator decision,
         2026-08-29). */
      padding:0 calc(12px + env(safe-area-inset-right,0)) calc(env(safe-area-inset-bottom,0) + 12px + var(--nav-h,0px)) calc(12px + env(safe-area-inset-left,0)); }
    .bar { pointer-events:auto; max-width:460px; width:100%; display:flex; flex-wrap:wrap; align-items:center;
      gap:8px 12px; padding:12px 14px;
      background:var(--app-surface,#fff); color:var(--app-text,#1d1b20); border:1px solid var(--app-outline,#cac4d0);
      border-radius:16px; box-shadow:var(--elev-3,0 10px 24px -8px rgba(16,18,27,.16)); font-size:13px; }
    /* In the width the FAB gutter leaves, text and buttons cannot share a
       line — the heading was wrapping word-per-line. The text keeps the top
       line; the actions drop to their own, right-aligned. */
    .bar .txt { flex:1 1 150px; min-width:0; }
    .bar .acts { display:flex; align-items:center; gap:4px; margin-left:auto; }
    .bar .ico { color:var(--app-primary,#6750a4); font-size:22px; }
    .bar strong { display:block; font-size:14px; }
  `;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('beforeinstallprompt', this.onBeforeInstall);
    window.addEventListener('appinstalled', this.onInstalled);
    // The event is Chrome's to give, and Chrome often declines: after a past
    // dismissal, right after an uninstall, and on browsers that never fire it
    // at all (Samsung Internet, MIUI, Opera — common defaults). Without a
    // fallback, "no event" meant NO install path anywhere in the app. Wait
    // long enough for a willing browser to fire, then show the manual route.
    this.fallbackTimer = window.setTimeout(() => { this.fallbackReady = true; }, PwaPrompt.fallbackDelayMs);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstall);
    window.removeEventListener('appinstalled', this.onInstalled);
    window.clearTimeout(this.fallbackTimer);
    // Stop reserving room for a bar that is no longer there.
    document.documentElement.style.setProperty('--install-bar-h', '0px');
  }

  private onBeforeInstall = (e: Event) => {
    e.preventDefault(); // defer; surface our own button
    this.installEvt = e as BeforeInstallPromptEvent;
  };
  private onInstalled = () => {
    this.installEvt = null;
  };

  /** Surface the update banner (called when a new SW is waiting). */
  showUpdate() {
    this.updateReady = true;
    this.dismissed = false;
  }

  private async install() {
    if (!this.installEvt) return;
    await this.installEvt.prompt();
    await this.installEvt.userChoice;
    this.installEvt = null;
  }

  private get isIOS(): boolean {
    return /iphone|ipad|ipod/i.test(navigator.userAgent);
  }
  private get isStandalone(): boolean {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true
    );
  }

  /**
   * Publish this bar's height as `--install-bar-h` on the document.
   *
   * The bar is position:fixed over the page, so it hides whatever is at the
   * bottom of the scroll — which was the last section, the company panel, with
   * its "Set up company cards" button underneath the banner. Reserving space
   * from a hardcoded number would go stale the moment the copy wraps to another
   * line or a language sets it differently, so the bar measures itself.
   */
  protected updated() {
    const bar = this.renderRoot.querySelector('.bar') as HTMLElement | null;
    const h = bar ? Math.ceil(bar.getBoundingClientRect().height) + 24 : 0;
    document.documentElement.style.setProperty('--install-bar-h', `${h}px`);
  }

  render() {
    if (this.dismissed) return nothing;

    if (this.updateReady) {
      return html`<div class="bar">
        ${icon('sync')}
        <span class="txt"><strong>${t('pwa.update')}</strong>${t('pwa.updateSub')}</span>
        <span class="acts"><md-filled-button @click=${this.onReload}>${t('pwa.reload')}</md-filled-button></span>
      </div>`;
    }

    if (this.installEvt) {
      return html`<div class="bar">
        ${icon('install_mobile')}
        <span class="txt"><strong>${t('pwa.install')}</strong>${t('pwa.installSub')}</span>
        <span class="acts">
          <md-text-button @click=${() => { this.dismissed = true; }}>${t('pwa.notNow')}</md-text-button>
          <md-filled-button @click=${this.install}>${t('pwa.installBtn')}</md-filled-button>
        </span>
      </div>`;
    }

    if (this.isIOS && !this.isStandalone) {
      return html`<div class="bar">
        ${icon('ios_share')}
        <span class="txt"><strong>${t('pwa.a2hs')}</strong>${t('pwa.a2hsSub')}</span>
        <span class="acts"><md-text-button @click=${() => { this.dismissed = true; }}>${t('pwa.gotIt')}</md-text-button></span>
      </div>`;
    }

    // Any other browser that has not offered the event by now: the manual
    // route, or the app cannot be installed from anywhere at all.
    if (this.fallbackReady && !this.isStandalone) {
      return html`<div class="bar">
        ${icon('install_mobile')}
        <span class="txt"><strong>${t('pwa.a2hs')}</strong>${t('pwa.a2hsMenu')}</span>
        <span class="acts"><md-text-button @click=${() => { this.dismissed = true; }}>${t('pwa.gotIt')}</md-text-button></span>
      </div>`;
    }

    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'pwa-prompt': PwaPrompt; }
}
